import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Dumbbell,
  GraduationCap,
  BookOpen,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  MapPin,
  Newspaper,
  Scale,
  Shield,
  Ticket,
  User,
  type LucideIcon,
} from "lucide-react";

import { useAuth } from "@/auth";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import NotificationPanel from "@/components/notifications/NotificationPanel";
import { useAdmin } from "@/components/admin/AdminGate";
import { useEntitlement } from "@/entitlement/EntitlementGateContext";
import useFirstPassUnlocked from "@/hooks/useFirstPassUnlocked";
import { useHasAccess } from "@/hooks/useHasAccess";
import { resolveAdminUrl } from "@/lib/admin-url";

/**
 * LearnerPortalShell — white-sidebar layout for the authenticated learner
 * surface (`/dashboard`, `/practice`, `/profile`, `/training`, etc.).
 *
 * Adopted from the 2026-05-04 Replit graduation under a stripped scope
 * (RB-G6, PR #299), then expanded to all auth pages and given a
 * collapsible-sidebar toggle in RB-G8.
 *
 * Nav restructure (this PR — feat/learner-nav-restructure):
 *   - Top-level: Dashboard, then an expandable "Courses" accordion group,
 *     then Blog and Support, then the conditional Administration link.
 *   - The Courses group is a toggle button (NOT a nav link) with a chevron
 *     that rotates on expand/collapse. Its children (Training, Practice,
 *     Exams, Checklists, Rules — plus the conditional "What's next" once
 *     the first-pass gate unlocks) render indented beneath it. Expand state
 *     persists in localStorage under `rb:learner-courses-expanded:v1`,
 *     default EXPANDED.
 *   - Profile is REMOVED from the top nav. The bottom user footer is now an
 *     accessible disclosure menu (My profile / Log out).
 *   - `isExpired` locking is preserved: gated children grey out; Dashboard,
 *     Blog, and Support stay always-reachable.
 *
 * Modifications vs the quarantine version:
 *   1. Outer wrapper is a `<div>` rather than `<main>` so it doesn't
 *      collide with App.tsx's a11y skip-target `<main id="main-content">`.
 *   2. All hardcoded English replaced with `learner.portal.*` i18n keys.
 *   3. The `<Bell>` notification button is wired to NotificationPanel.
 *   4. Collapsible sidebar (RB-G8): chevron toggle in the header bar flips
 *      between full (`w-60`) and icons-only (`w-14`); state persisted under
 *      `rb:learner-sidebar-collapsed:v1`. Icons-only mode hides text
 *      labels, keeps icons + tooltips.
 */
interface LearnerPortalShellProps {
  /** Page title shown in the desktop top header bar. */
  pageTitle: string;
  /** Optional right-hand slot in the header (e.g. state badge / state switcher). */
  headerRight?: ReactNode;
  children: ReactNode;
}

interface NavItem {
  to: string;
  /** i18n key under `learner.portal.nav` (e.g. "dashboard"). */
  i18nKey: string;
  Icon: LucideIcon;
  /** When true, NavLink uses `end` matching (root-of-section). */
  exact?: boolean;
  /** Optional explicit data-testid override (defaults derive from `to`). */
  testId?: string;
}

/** Top-level standalone links (rendered above + below the Courses group). */
const DASHBOARD_ITEM: NavItem = {
  to: "/dashboard",
  i18nKey: "dashboard",
  Icon: LayoutDashboard,
  exact: true,
};

/**
 * "My pass" — the vacationer trip-pass surface (`/pass`). Conditionally
 * rendered just below Dashboard ONLY when the user holds an active
 * `tourist_out` entitlement. Without this the learner portal has no path to a
 * purchased pass at all, so a buyer could never reach the content they bought
 * (ROADS-576). The `/pass/*` routes own their own paywall, but the nav entry
 * is gated on real ownership so non-buyers never see it.
 */
const MY_PASS_ITEM: NavItem = {
  to: "/pass",
  i18nKey: "myPass",
  Icon: Ticket,
  testId: "learner-nav-my-pass",
};

const TOP_LEVEL_LINKS: readonly NavItem[] = [
  { to: "/blog", i18nKey: "blog", Icon: Newspaper, testId: "learner-nav-blog" },
  {
    to: "/support",
    i18nKey: "support",
    Icon: LifeBuoy,
    testId: "learner-nav-support",
  },
];

/**
 * Children of the "Courses" accordion group, in journey order. The
 * conditional "What's next" item is spliced in after Exams once the
 * first-pass gate unlocks (see `coursesChildren` below).
 */
const COURSES_CHILDREN: readonly NavItem[] = [
  { to: "/training", i18nKey: "training", Icon: GraduationCap },
  { to: "/practice", i18nKey: "practice", Icon: Dumbbell },
  { to: "/exam/history", i18nKey: "exams", Icon: ClipboardList },
  { to: "/checklists", i18nKey: "checklists", Icon: ListChecks },
  { to: "/rule-diff/browse", i18nKey: "rules", Icon: Scale },
];

/**
 * The post-pass nav item is conditionally rendered after a first-pass gate
 * (ROADS-431). It is positioned immediately after "Exams" / `/exam/history`
 * per the journey-arc specification: the user just passed an exam → the
 * next thing they want is the roadmap. Now lives inside the Courses group.
 */
const WHATS_NEXT_NAV_ITEM: NavItem = {
  to: "/whats-next",
  i18nKey: "whatsNext",
  Icon: MapPin,
};
const WHATS_NEXT_INSERT_AFTER: string = "/exam/history";

/**
 * The routes the learner can ALWAYS reach. When entitlements lapse, every
 * other nav item is locked (greyed-out, non-clickable) so the user is
 * funnelled to the conversion surfaces. Anchored to the route path (not the
 * i18nKey) so future renames don't silently disable the gate.
 *
 *   - `/dashboard`  — primary conversion surface.
 *   - `/profile`    — reachable via the user menu, not the top nav.
 *   - `/blog`, `/support` — informational, never gated.
 */
const ALWAYS_REACHABLE_PATHS: ReadonlySet<string> = new Set([
  "/dashboard",
  "/profile",
  "/blog",
  "/support",
  // The pass is the user's own purchase — never grey it out when an expired
  // class_c trial flips `isExpired`; the entry only shows when owned anyway.
  "/pass",
]);

const COLLAPSED_STORAGE_KEY = "rb:learner-sidebar-collapsed:v1";
const COURSES_EXPANDED_STORAGE_KEY = "rb:learner-courses-expanded:v1";

function readBoolFromStorage(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    return raw === "true";
  } catch {
    return fallback;
  }
}

function writeBoolToStorage(key: string, value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // localStorage may be unavailable (private browsing, quota) — UI still
    // toggles for the session, just doesn't persist.
  }
}

/**
 * Administration nav entry. Conditional render is the caller's job; this
 * component handles the absolute-vs-relative URL decision via
 * `resolveAdminUrl`. When `VITE_ADMIN_URL` is unset (dev / unified hostname
 * mode), we render a `<NavLink>` for same-origin SPA routing. When it's set
 * to an `http(s)://` origin (Phase 1A of ADR-0013 — admin isolated on a
 * separate subdomain), we render a plain `<a>` so the navigation does a full
 * page load to the admin origin and the admin SPA boots fresh.
 */
function AdminLink({ isCollapsed }: { isCollapsed: boolean }) {
  const envValue = (import.meta.env as Record<string, string | undefined>)
    .VITE_ADMIN_URL;
  const { href, external } = resolveAdminUrl("/admin/users", envValue);

  const innerClasses = `flex items-center rounded-xl text-sm font-medium transition-colors no-underline ${
    isCollapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
  } text-red-700 hover:bg-red-50`;

  const inner = (
    <>
      <Shield aria-hidden="true" size={17} className="text-red-600" />
      {!isCollapsed && "Administration"}
    </>
  );

  return (
    <>
      <hr
        aria-hidden="true"
        className={`my-2 border-slate-100 ${isCollapsed ? "mx-1" : "mx-2"}`}
      />
      {external ? (
        <a
          href={href}
          title={isCollapsed ? "Administration" : undefined}
          aria-label={isCollapsed ? "Administration" : undefined}
          data-testid="learner-portal-admin-link"
          data-external="true"
          className={innerClasses}
        >
          {inner}
        </a>
      ) : (
        <NavLink
          to={href}
          title={isCollapsed ? "Administration" : undefined}
          aria-label={isCollapsed ? "Administration" : undefined}
          data-testid="learner-portal-admin-link"
          className={({ isActive }) =>
            `${innerClasses} ${isActive ? "bg-red-50 font-semibold" : ""}`
          }
        >
          {inner}
        </NavLink>
      )}
    </>
  );
}

/**
 * Render a single learner NavLink (or a locked, greyed-out span when the
 * entitlement has lapsed and the path isn't always-reachable). Shared by the
 * top-level links and the Courses-group children. `indented` adds left
 * padding for the accordion children (expanded mode only).
 */
function NavItemLink({
  item,
  isCollapsed,
  isExpired,
  indented,
}: {
  item: NavItem;
  isCollapsed: boolean;
  isExpired: boolean;
  indented: boolean;
}) {
  const { t } = useTranslation();
  const { to, i18nKey, Icon, exact, testId } = item;
  const label = t(`learner.portal.nav.${i18nKey}`);
  const isLocked = isExpired && !ALWAYS_REACHABLE_PATHS.has(to);

  // Indentation only applies in expanded mode; collapsed mode centres icons.
  const padX = isCollapsed
    ? "justify-center px-2 py-2.5"
    : indented
      ? "gap-3 pl-8 pr-3 py-2.5"
      : "gap-3 px-3 py-2.5";

  if (isLocked) {
    return (
      <span
        key={to}
        aria-disabled="true"
        data-testid={testId}
        title={isCollapsed ? label : undefined}
        className={`flex items-center rounded-xl text-sm font-medium cursor-not-allowed select-none opacity-35 ${padX} text-slate-500`}
      >
        <Icon aria-hidden="true" size={17} className="text-slate-400" />
        {!isCollapsed && label}
      </span>
    );
  }

  return (
    <NavLink
      key={to}
      to={to}
      end={exact}
      data-testid={testId}
      title={isCollapsed ? label : undefined}
      aria-label={isCollapsed ? label : undefined}
      className={({ isActive }) =>
        `flex items-center rounded-xl text-sm font-medium transition-colors no-underline ${padX} ${
          isActive
            ? "bg-teal-50 text-teal-700 font-semibold"
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            aria-hidden="true"
            size={17}
            className={isActive ? "text-teal-600" : "text-slate-400"}
          />
          {!isCollapsed && label}
        </>
      )}
    </NavLink>
  );
}

/**
 * Accessible disclosure menu for the user footer. Trigger is a `<button>`
 * with `aria-haspopup="menu"` + `aria-expanded`; the popup is a
 * `role="menu"` container of `role="menuitem"` items. Closes on Escape and
 * outside-click; arrow keys move focus between items.
 */
function UserMenu({
  displayName,
  email,
  initial,
  isCollapsed,
}: {
  displayName: string;
  email: string;
  initial: string;
  isCollapsed: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Outside-click + Escape close. Bound only while the menu is open.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Focus the first item when the menu opens.
  useEffect(() => {
    if (open) {
      itemRefs.current[0]?.focus();
    }
  }, [open]);

  const handleMyProfile = useCallback(() => {
    setOpen(false);
    navigate("/profile");
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    setOpen(false);
    await logout();
    navigate("/", { replace: true });
  }, [logout, navigate]);

  function onItemsKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const items = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
    if (items.length === 0) return;
    const currentIndex = items.findIndex(
      (el) => el === document.activeElement,
    );
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = items[(currentIndex + 1) % items.length];
      next?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = items[(currentIndex - 1 + items.length) % items.length];
      prev?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    }
  }

  const triggerLabel = `${displayName}${email ? ` — ${email}` : ""}`.trim();

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid="learner-user-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={isCollapsed ? t("learner.portal.userMenu.open") : undefined}
        title={isCollapsed ? triggerLabel : undefined}
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-700 hover:bg-neutral-100 ${
          isCollapsed ? "justify-center px-2 py-2" : "gap-3 px-2 py-2"
        }`}
      >
        <span
          aria-hidden="true"
          className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-sm font-bold text-teal-700 shrink-0"
        >
          {initial}
        </span>
        {!isCollapsed && (
          <span className="flex-1 min-w-0 text-start">
            <span className="block text-sm font-semibold text-slate-900 truncate">
              {displayName}
            </span>
            {/* GH #326: `text-neutral-600` (#475569 on white = 7.58:1, AA). */}
            <span className="block text-xs text-neutral-600 truncate">
              {email}
            </span>
          </span>
        )}
        {!isCollapsed && (
          <ChevronDown
            aria-hidden="true"
            size={16}
            className={`text-neutral-600 shrink-0 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label={t("learner.portal.userMenu.open")}
          data-testid="learner-user-menu"
          onKeyDown={onItemsKeyDown}
          className={`absolute bottom-full z-20 mb-2 min-w-[12rem] rounded-xl border border-slate-200 bg-white py-1 shadow-lg ${
            isCollapsed ? "left-0" : "inset-inline-start-0 right-0"
          }`}
        >
          <button
            ref={(el) => {
              itemRefs.current[0] = el;
            }}
            type="button"
            role="menuitem"
            data-testid="learner-user-menu-myprofile"
            onClick={handleMyProfile}
            className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none"
          >
            <User aria-hidden="true" size={16} className="text-slate-400" />
            {t("learner.portal.userMenu.myProfile")}
          </button>
          <button
            ref={(el) => {
              itemRefs.current[1] = el;
            }}
            type="button"
            role="menuitem"
            data-testid="learner-user-menu-logout"
            onClick={() => void handleLogout()}
            className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100 focus-visible:bg-neutral-100 focus-visible:outline-none"
          >
            <ChevronRight
              aria-hidden="true"
              size={16}
              className="text-neutral-400"
            />
            {t("learner.portal.userMenu.logout")}
          </button>
        </div>
      )}
    </div>
  );
}

export default function LearnerPortalShell({
  children,
  pageTitle,
  headerRight,
}: LearnerPortalShellProps) {
  const { t, i18n } = useTranslation();
  const { state } = useAuth();
  const user = state.kind === "authenticated" ? state.user : null;
  const fallbackName = t("learner.portal.userFallback");
  const displayName =
    user?.first_name?.trim() || user?.email?.split("@")[0] || fallbackName;
  const initial = (
    user?.first_name?.[0] ||
    user?.email?.[0] ||
    fallbackName[0] ||
    "L"
  ).toUpperCase();

  const firstPass = useFirstPassUnlocked();
  const { isExpired } = useEntitlement();
  const { isAdmin } = useAdmin();

  // Show the "My pass" entry only when the user actually owns an active pass.
  // The tier-only check returns true for any active tourist_out country pack
  // (the gate fix in ROADS-576). Non-owners never see the link.
  const passAccess = useHasAccess({ tier: "tourist_out" });
  const hasPass = passAccess.kind === "ready" && passAccess.hasAccess;

  // Conditional "What's next" insertion after `/exam/history` once the
  // learner has at least one passed exam attempt (ROADS-431). When the gate
  // is still loading OR the trial is expired we leave the item out.
  //
  // When `isExpired`, locked children remain visible but greyed-out so users
  // can see what they're missing — `<RequiresEntitlement>` still redirects if
  // users navigate directly via URL.
  const coursesChildren: readonly NavItem[] = (() => {
    if (!firstPass.unlocked || isExpired) return COURSES_CHILDREN;
    const idx = COURSES_CHILDREN.findIndex(
      (item) => item.to === WHATS_NEXT_INSERT_AFTER,
    );
    if (idx < 0) return [...COURSES_CHILDREN, WHATS_NEXT_NAV_ITEM];
    return [
      ...COURSES_CHILDREN.slice(0, idx + 1),
      WHATS_NEXT_NAV_ITEM,
      ...COURSES_CHILDREN.slice(idx + 1),
    ];
  })();

  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [coursesExpanded, setCoursesExpanded] = useState<boolean>(true);
  const [notifOpen, setNotifOpen] = useState(false);
  // Read persisted state in an effect so SSR (where `window` is undefined)
  // renders the defaults (expanded sidebar, expanded Courses) and hydration
  // doesn't diverge from server output.
  useEffect(() => {
    setIsCollapsed(readBoolFromStorage(COLLAPSED_STORAGE_KEY, false));
    setCoursesExpanded(readBoolFromStorage(COURSES_EXPANDED_STORAGE_KEY, true));
  }, []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      writeBoolToStorage(COLLAPSED_STORAGE_KEY, next);
      return next;
    });
  }, []);

  const toggleCoursesExpanded = useCallback(() => {
    setCoursesExpanded((prev) => {
      const next = !prev;
      writeBoolToStorage(COURSES_EXPANDED_STORAGE_KEY, next);
      return next;
    });
  }, []);

  const toggleLabel = isCollapsed
    ? t("learner.portal.expandSidebar")
    : t("learner.portal.collapseSidebar");

  // In collapsed (icons-only) mode the accordion chrome is dropped — the
  // Courses children render as a flat icon list, like every other nav icon.
  const showCoursesChildren = isCollapsed || coursesExpanded;

  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
      {/* ── LEFT SIDEBAR ──────────────────────────────────────────── */}
      <aside
        aria-label={t("learner.portal.navLabel")}
        data-collapsed={isCollapsed}
        className={`hidden md:flex shrink-0 flex-col bg-white border-r border-slate-200 transition-[width] duration-200 ${
          isCollapsed ? "w-14" : "w-60"
        }`}
      >
        {/* Brand */}
        <div
          className={`flex items-center border-b border-slate-100 ${
            isCollapsed ? "justify-center px-0 py-5" : "gap-2.5 px-5 py-5"
          }`}
        >
          <Link
            to="/dashboard"
            className="flex items-center gap-2.5 no-underline"
            aria-label="RoadBridge"
          >
            <img
              src="/favicon.png"
              alt=""
              aria-hidden="true"
              className="w-8 h-8 rounded-lg shrink-0"
            />
            {!isCollapsed && (
              <div>
                <p className="font-black text-base text-slate-900 leading-none">
                  RoadBridge
                </p>
                {/* GH #326: `text-neutral-600` (#475569 on white = 7.58:1, AA). */}
                <p className="text-[10px] text-neutral-600 font-medium mt-0.5">
                  {t("learner.portal.brandTagline")}
                </p>
              </div>
            )}
          </Link>
        </div>

        {/* Nav */}
        <nav
          className={`flex-1 py-4 space-y-0.5 overflow-y-auto ${
            isCollapsed ? "px-2" : "px-3"
          }`}
        >
          {/* Dashboard — top-level */}
          <NavItemLink
            item={DASHBOARD_ITEM}
            isCollapsed={isCollapsed}
            isExpired={isExpired}
            indented={false}
          />

          {/* My pass — only when the user owns an active trip pass. */}
          {hasPass && (
            <NavItemLink
              item={MY_PASS_ITEM}
              isCollapsed={isCollapsed}
              isExpired={isExpired}
              indented={false}
            />
          )}

          {/* Courses — accordion group. The toggle is a button, not a link. */}
          {!isCollapsed && (
            <button
              type="button"
              data-testid="learner-nav-courses-toggle"
              aria-expanded={coursesExpanded}
              aria-controls="learner-nav-courses-group"
              aria-label={t("learner.portal.nav.coursesToggle")}
              onClick={toggleCoursesExpanded}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700"
            >
              <BookOpen
                aria-hidden="true"
                size={17}
                className="text-slate-400"
              />
              <span className="flex-1 text-start">
                {t("learner.portal.nav.courses")}
              </span>
              <ChevronDown
                aria-hidden="true"
                size={16}
                className={`text-slate-400 transition-transform ${
                  coursesExpanded ? "rotate-180" : ""
                }`}
              />
            </button>
          )}

          {/* In collapsed mode there's no accordion header — the group icon
              would just duplicate BookOpen, so we drop it and render the
              children directly as a flat icon list. */}
          {showCoursesChildren && (
            <div
              id="learner-nav-courses-group"
              className={isCollapsed ? "space-y-0.5" : "space-y-0.5"}
            >
              {coursesChildren.map((item) => (
                <NavItemLink
                  key={item.to}
                  item={item}
                  isCollapsed={isCollapsed}
                  isExpired={isExpired}
                  indented={!isCollapsed}
                />
              ))}
            </div>
          )}

          {/* Blog + Support — top-level links below the Courses group. */}
          {TOP_LEVEL_LINKS.map((item) => (
            <NavItemLink
              key={item.to}
              item={item}
              isCollapsed={isCollapsed}
              isExpired={isExpired}
              indented={false}
            />
          ))}

          {/*
            Administration entry — only rendered when `useAdmin()` resolves to
            true (the AdminGate probe returned 200). Visually distinct (red
            accent) to signal "you are leaving the learner surface and
            entering an ops console with destructive actions". Lives at the
            bottom of the nav, after a thin divider.
          */}
          {isAdmin && <AdminLink isCollapsed={isCollapsed} />}
        </nav>

        {/* User footer — disclosure menu (My profile / Log out). */}
        <div
          className={`border-t border-slate-100 ${
            isCollapsed ? "px-2 py-3" : "px-3 py-3"
          }`}
        >
          <UserMenu
            displayName={displayName}
            email={user?.email ?? ""}
            initial={initial}
            isCollapsed={isCollapsed}
          />
        </div>
      </aside>

      {/* ── RIGHT: HEADER + CONTENT ───────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="shrink-0 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-5 sticky top-0 z-10">
          <div className="flex items-center gap-2 min-w-0">
            {/* Sidebar collapse toggle — desktop only (matches sidebar visibility) */}
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={toggleLabel}
              aria-expanded={!isCollapsed}
              className="hidden md:inline-flex items-center justify-center p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700"
            >
              {isCollapsed ? (
                <ChevronRight aria-hidden="true" size={18} />
              ) : (
                <ChevronLeft aria-hidden="true" size={18} />
              )}
            </button>
            {/* Mobile-only brand chip (sidebar is hidden below md:) */}
            <div className="flex items-center gap-2 md:hidden">
              <img
                src="/favicon.png"
                alt=""
                aria-hidden="true"
                className="w-7 h-7 rounded-lg"
              />
              <span className="font-black text-base text-slate-900">
                RoadBridge
              </span>
            </div>
            {/* Desktop title */}
            <h1 className="hidden md:block text-base font-semibold text-slate-800 truncate">
              {pageTitle}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {headerRight}
            <LanguageSwitcher
              currentLang={i18n.language}
              onChange={(l) => void i18n.changeLanguage(l)}
              variant="compact"
            />
            <div className="relative">
              <button
                type="button"
                aria-label={t("learner.portal.notifications")}
                aria-expanded={notifOpen}
                aria-controls="notification-panel"
                onClick={() => setNotifOpen((o) => !o)}
                className="relative p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700"
              >
                <Bell aria-hidden="true" size={18} />
              </button>
              <div id="notification-panel">
                <NotificationPanel
                  isOpen={notifOpen}
                  onClose={() => setNotifOpen(false)}
                />
              </div>
            </div>
            {/* Mobile-only avatar (sidebar is hidden below md:) */}
            <div
              aria-hidden="true"
              className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-sm font-bold text-teal-700 md:hidden"
            >
              {initial}
            </div>
          </div>
        </header>

        {/* Scrollable content. NB: not <main> — App.tsx already wraps the
            entire route tree in <main id="main-content"> for the a11y
            skip-link target. Two <main>s per document is invalid HTML. */}
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
